<script setup lang="ts">
const props = defineProps<{
  name: string,
  player: 'player_a' | 'player_b'
}>()

const { game } = useBoard(props.name)

interface PlayerItemProps {
  renjuNetId: string,
  name: string,
  surname: string,
  fullname: string,
  nativeName: string
}

const selectedPlayer = ref<PlayerItemProps>()
const items = ref<PlayerItemProps[]>([])
const mine = ref<PlayerProps>(props.player === 'player_a' ? game.value.playerA : game.value.playerB)
const opponent = ref<PlayerProps>(props.player === 'player_a' ? game.value.playerB : game.value.playerA)

const handleUpdatePlayerColor = (newValue: string) => {
  opponent.value.color = newValue === 'black' ? 'white' : 'black'
}

function handleClickAddSeconds () {
  const datetime = new Date(`2000-01-01T${mine.value.remainTime || '00:00:00'}`)
  datetime.setSeconds(datetime.getSeconds() + 10)
  mine.value.remainTime = datetime.toTimeString().slice(0, 8)
}

function handleClickBackSeconds () {
  const datetime = new Date(`2000-01-01T${mine.value.remainTime || '00:00:00'}`)
  datetime.setSeconds(datetime.getSeconds() - 10)
  mine.value.remainTime = datetime.toTimeString().slice(0, 8)
}

function handleSearch (text: string) {
  if (text.length >= 3) {
    (async () => {
      const response = await $fetch<{ players: PlayerItemProps[] }>('/api/renjunet/player', {
        query: {
          query: text
        }
      })
      items.value = response.players.map(player => ({
        renjuNetId: player.renjuNetId,
        name: player.name,
        surname: player.surname,
        fullname: `${player.name} ${player.surname}`,
        nativeName: player.nativeName
      }))
    })()
  } else {
    items.value = []
  }
}

function handleUpdatePlayer (newValue: PlayerItemProps) {
  if (!newValue) {
    mine.value.renjuNetId = ''
    mine.value.name = ''
    mine.value.englishName = ''
    return
  }

  mine.value.renjuNetId = newValue.renjuNetId
  if (newValue.nativeName) {
    mine.value.name = newValue.nativeName
    mine.value.englishName = newValue.fullname
  } else {
    mine.value.name = newValue.fullname
    mine.value.englishName = ''
  }
}

watch(mine, async (newValue) => {
  if (newValue.renjuNetId) {
    const response = await $fetch<{ players: PlayerItemProps[] }>('/api/renjunet/player', {
      query: {
        renjuNetId: newValue.renjuNetId
      }
    })
    items.value = items.value.concat(response.players.map(player => ({
      renjuNetId: player.renjuNetId,
      name: player.name,
      surname: player.surname,
      fullname: `${player.name} ${player.surname}`,
      nativeName: player.nativeName
    })))
    if (!selectedPlayer.value?.renjuNetId) {
      selectedPlayer.value = {
        renjuNetId: response.players[0].renjuNetId,
        name: response.players[0].name,
        surname: response.players[0].surname,
        fullname: `${response.players[0].name} ${response.players[0].surname}`,
        nativeName: response.players[0].nativeName
      }
    }
  }
}, { immediate: true, deep: true })
</script>

<template>
  <v-window-item :value="player">
    <v-card flat>
      <v-card-text>
        <v-autocomplete
          v-model="selectedPlayer"
          label="対局者 (RenjuNet)"
          :items="items"
          item-title="fullname"
          item-value="fullname"
          return-object
          no-data-text="検索結果はありません。"
          clearable
          variant="outlined"
          dirty
          @update:search="handleSearch"
          @update:model-value="handleUpdatePlayer"
        >
          <template #item="{ props, item }">
            <v-list-item
              v-bind="props"
              :title="item?.raw?.fullname"
              :subtitle="item.raw?.nativeName"
            />
          </template>
        </v-autocomplete>
        <v-text-field
          v-model="mine.name"
          label="対局者名"
          variant="outlined"
          :disabled="Boolean(selectedPlayer)"
          dirty
        />
        <v-text-field
          v-model="mine.englishName"
          label="対局者名(英語)"
          variant="outlined"
          :disabled="Boolean(selectedPlayer)"
          dirty
        />
        <v-row dense>
          <v-col cols="12" sm="4">
            <v-select
              v-model="mine.color"
              label="手番"
              :items="[
                { label: '黒', value: 'black' },
                { label: '白', value: 'white' }
              ]"
              item-title="label"
              item-value="value"
              variant="outlined"
              dirty
              @update:model-value="handleUpdatePlayerColor"
            />
          </v-col>
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="mine.remainTime"
              label="残り時間"
              variant="outlined"
              type="time"
              clearable
            >
              <template #append>
                <v-btn
                  icon=""
                  variant="text"
                  size="small"
                  @click="handleClickAddSeconds"
                >
                  +10
                </v-btn>
                <v-btn
                  icon=""
                  variant="text"
                  size="small"
                  @click="handleClickBackSeconds"
                >
                  -10
                </v-btn>
              </template>
            </v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-window-item>
</template>
